import React, { useEffect, useState } from "react";
import {
    Button,
    Col,
    DatePicker,
    Row,
    Select,
    Spin,
    Tooltip,
    Card,
    Form,
} from "antd";
import { getCommonDistributionDetails, getCommonDistributionListByBranchList } from "./GeneralService";
import { errHandler } from "../../helper/helper";
import { commonDistributionExcel } from "../../Utils/commonDistributionExcel";
import { headerStyle, subtotalStyle, totalStyle } from "../../Utils/excelStyle";

const { RangePicker } = DatePicker;
const { Option } = Select;

const CommonDistribution = ({
    loginPlaceId,
    districtName,
    districtDivisionalBranchData,
    divisionalId
}) => {

    const [formState, setFormState] = useState({
        districts: [],
        divisionalAreas: [],
        branches: [],
        date: [],
    });

    const [availableDivisionalAreas, setAvailableDivisionalAreas] = useState([]);
    const [availableBranches, setAvailableBranches] = useState([]);
    const [loading, setLoading] = useState(false);
    const [commonDistributionList, setCommonDistributionList] = useState([]);
    const [selectedCommonDistributions, setSelectedCommonDistributions] = useState([]);



    const updateFormState = (field, value) => {
        setFormState((prev) => ({ ...prev, [field]: value }));
    };

    const isGenerateDisabled =
        !formState.branches.length ||
        !formState.divisionalAreas.length ||
        formState.date.length === 0;

    const getSelectedDistrictNames = () => {
        return districtDivisionalBranchData
            .filter((d) => formState.districts.includes(d.districtId))
            .map((d) => d.districtName)
            .join(", ");
    };

    // ✅ Get Divisional area names (for report title/export)
    const getSelectedDivisionalAreaNames = () => {
        return districtDivisionalBranchData
            .flatMap((d) => d.divisionalAreaDtoList || [])
            .filter((div) => formState.divisionalAreas.includes(div.divAId))
            .map((div) => div.divAIdName)
            .join(", ");
    };

    const handleBookEntryListApi = () => {
        setLoading(true);
        // Add your API logic here
        let data = {
            branchIdList: formState.branches,
            startDate: formState.date[0].format("YYYY-MM-DD"),
            endDate: formState.date[1].format("YYYY-MM-DD"),
            distributionName: selectedCommonDistributions
        }
        const title = loginPlaceId === "2" ? getSelectedDivisionalAreaNames() : getSelectedDistrictNames();
        const header = `${title} - Common Distribution Report - ${selectedCommonDistributions}`;

        getCommonDistributionDetails(data).then((res) => {
            const reportData = res?.data || res;
            if (reportData.length === 0) {
                errHandler("No data found for the selected criteria.");
                return;
            }
            commonDistributionExcel(reportData, headerStyle, subtotalStyle, totalStyle, header);
        }).catch((err) => {
            errHandler(err);
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        let filteredDivisionalAreas = [];

        if (loginPlaceId === "2" && divisionalId) {
            filteredDivisionalAreas = districtDivisionalBranchData
                .flatMap((d) => d.divisionalAreaDtoList || [])
                .filter((div) => div.divAId === divisionalId);

            updateFormState("divisionalAreas", [divisionalId]);
        } else {
            filteredDivisionalAreas = districtDivisionalBranchData
                .filter((d) => formState.districts.includes(d.districtId))
                .flatMap((d) => d.divisionalAreaDtoList || []);

            updateFormState(
                "divisionalAreas",
                formState.divisionalAreas.filter((did) =>
                    filteredDivisionalAreas.some((d) => d.divAId === did)
                )
            );
        }

        setAvailableDivisionalAreas(filteredDivisionalAreas);
    }, [formState.districts, districtDivisionalBranchData, loginPlaceId, divisionalId]);

    // ✅ Update branches when divisional areas change
    useEffect(() => {
        const filteredBranches = districtDivisionalBranchData
            .flatMap((d) => d.divisionalAreaDtoList || [])
            .filter((div) => formState.divisionalAreas.includes(div.divAId))
            .flatMap((div) => div.branchDtoList || []);

        setAvailableBranches(filteredBranches);
        updateFormState(
            "branches",
            formState.branches.filter((bid) =>
                filteredBranches.some((b) => b.id === bid)
            )
        );
    }, [formState.divisionalAreas, districtDivisionalBranchData]);

    useEffect(() => {
        if (formState.branches.length > 0) {
            // Fetch distribution names based on selected branches
            let data={
                branchIdList: formState.branches
            }
            getCommonDistributionListByBranchList(data)
                .then((res) => {
                    const list = res?.data || res || [];
                    setCommonDistributionList(list);
                    setSelectedCommonDistributions([]); // reset selection
                })
                .catch((err) => {
                    errHandler(err);
                    setCommonDistributionList([]);
                });
        } else {
            setCommonDistributionList([]);
            setSelectedCommonDistributions([]);
        }
    }, [formState.branches]);


    return (
        <div className="panel-container">
            <Card hoverable className="inner-card">
                <Form layout="vertical">
                    <Row gutter={[16, 16]} justify="space-around">
                        {(loginPlaceId === "3" || loginPlaceId === "4") && (
                            <Col span={24}>
                                <Form.Item label="Select Districts">
                                    <Select
                                        mode="multiple"
                                        placeholder="Select districts"
                                        style={{ width: "100%" }}
                                        value={formState.districts}
                                        onChange={(values) =>
                                            updateFormState("districts", values)
                                        }
                                    >
                                        {districtDivisionalBranchData
                                            .filter((d) =>
                                                loginPlaceId === "4"
                                                    ? d.districtName ===
                                                    districtName
                                                    : true
                                            )
                                            .map((d) => (
                                                <Option
                                                    key={d.districtId}
                                                    value={d.districtId}
                                                >
                                                    🏙️ {d.districtName}
                                                </Option>
                                            ))}
                                    </Select>
                                </Form.Item>
                            </Col>
                        )}

                        <Col span={24}>
                            <Form.Item label="Select Divisional Areas">
                                <Select
                                    mode="multiple"
                                    placeholder="Select divisional areas"
                                    style={{ width: "100%" }}
                                    value={formState.divisionalAreas}
                                    onChange={(values) => {
                                        if (values.includes("ALL")) {
                                            updateFormState(
                                                "divisionalAreas",
                                                availableDivisionalAreas.map(
                                                    (d) => d.divAId
                                                )
                                            );
                                        } else {
                                            updateFormState(
                                                "divisionalAreas",
                                                values
                                            );
                                        }
                                    }}
                                    disabled={
                                        availableDivisionalAreas.length === 0
                                    }
                                >
                                    {(loginPlaceId === "3" ||
                                        loginPlaceId === "4") && (
                                            <Option key="ALL" value="ALL">
                                                🔘 Select All Divisional Areas
                                            </Option>
                                        )}
                                    {availableDivisionalAreas.map((d) => (
                                        <Option key={d.divAId} value={d.divAId}>
                                            🏙️ {d.divAIdName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>

                        <Col span={24}>
                            <Form.Item label="Select Branch(es)">
                                <Select
                                    mode="multiple"
                                    placeholder="Select branches"
                                    style={{ width: "100%" }}
                                    value={formState.branches}
                                    onChange={(values) => {
                                        if (values.includes("ALL")) {
                                            updateFormState(
                                                "branches",
                                                availableBranches.map(
                                                    (b) => b.id
                                                )
                                            );
                                        } else {
                                            updateFormState("branches", values);
                                        }
                                    }}
                                    disabled={availableBranches.length === 0}
                                >
                                    <Option key="ALL" value="ALL">
                                        🔘 Select All Branches
                                    </Option>
                                    {availableBranches.map((branch) => (
                                        <Option
                                            key={branch.id}
                                            value={branch.id}
                                        >
                                            🏢 {branch.branchName}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                        <Col span={24}>
                            <Form.Item label="Select Common Distribution List">
                                <Select
                                    placeholder="Select common distribution name(s)"
                                    style={{ width: "100%" }}
                                    value={selectedCommonDistributions}
                                    onChange={(values) => setSelectedCommonDistributions(values)}
                                    disabled={commonDistributionList.length === 0}
                                >
                                    {commonDistributionList.map((name) => (
                                        <Option key={name} value={name}>
                                            🗂️ {name}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>


                        <Col span={24}>
                            <Form.Item label="Select Month Range">
                                <RangePicker picker="date" style={{ width: "100%" }} onChange={(dates) => updateFormState("date", dates)} />
                            </Form.Item>
                        </Col>



                        <Col span={24} style={{ textAlign: "right" }}>
                            <Form.Item className="generate-btn-outline">
                                <Button
                                    onClick={handleBookEntryListApi}
                                    disabled={loading || isGenerateDisabled}
                                    className="generate-btn"
                                >
                                    {loading ? <Spin /> : "Generate Report"}
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                </Form>
            </Card>
        </div>
    );
};

export default CommonDistribution;